<template>
  <div class="flex flex-wrap items-center gap-4">
    <RevTag
      v-if="shouldDisplayStatusTag"
      class="truncate"
      data-test="order-details-status"
      :label="statusTagLabel"
      :size="isSmallScreen ? 'small' : 'medium'"
      :variant="statusTagVariant"
    />
    <RevTag
      v-if="shouldDisplayReturnStatusTag"
      class="truncate"
      data-test="order-details-return-status"
      :label="returnStatusTagLabel"
      :size="isSmallScreen ? 'small' : 'medium'"
      variant="info"
    />
    <RevTag
      v-if="shouldDisplayRepairStatusTag"
      class="truncate"
      data-test="order-details-repair-status"
      :label="repairStatusTagLabel"
      :size="isSmallScreen ? 'small' : 'medium'"
      variant="success"
    />
    <RevTag
      v-if="shouldDisplayPartnershipTag"
      class="truncate"
      data-test="order-details-partnership"
      :label="i18n(translations.replacementOrder)"
      :size="isSmallScreen ? 'small' : 'medium'"
      variant="warning"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { RevTag } from '@ds/components/Tag'
import { useMediaQuery } from '@vueuse/core'

import {
  type CareFolder,
  type Orderline,
  type ProductReturn,
  type ProductReturnState,
  productRepairStatuses,
  productReturnStates,
} from '~/api/care-folders/types/care-folder'
import { ORDERLINE_STATES } from '~/api/care-folders/types/orderline-states.types'

import translations from './StatusTags.translations'

const i18n = useI18n()
const logger = useLogger()

const props = defineProps<{
  orderline: Orderline
  productReturnKind?: ProductReturn['kind']
  productReturnState: ProductReturnState
  repairStatus: CareFolder['repairStatus']
}>()

const hasProductReturn = computed(
  () =>
    props.productReturnState !== 'NO_PRODUCT_RETURN' &&
    props.productReturnState !== 'PRODUCT_RETURN_CREATED',
)
const hasActiveProductReturn = computed(
  () =>
    hasProductReturn.value &&
    props.productReturnState !== 'PRODUCT_RETURN_EXPIRED',
)
const isOrderlineRefunded = computed(
  () =>
    props.orderline.state === 'REFUNDED_POST_DELIVERY' ||
    props.orderline.state === 'REFUNDED_PRE_DELIVERY',
)

const shouldDisplayStatusTag = computed(() => {
  if (Object.values(ORDERLINE_STATES).includes(props.orderline.state)) {
    return (
      (!hasProductReturn.value || isOrderlineRefunded.value) &&
      props.orderline.state !== 'NEW' &&
      props.orderline.state !== 'NOT_PAID'
    )
  }

  logger.info(
    `[ASP Conversation Summary] Orderline state '${props.orderline.state}' is not supported.`,
    {
      owners: ['bot-squad-seller-care-front'],
    },
  )

  return false
})
const statusTagLabel = computed(() => {
  if (isOrderlineRefunded.value) {
    return i18n(translations.refunded)
  }

  switch (props.orderline.state) {
    case 'CANCELLED':
      return i18n(translations.cancelled)

    case 'SHIPPED':
      return i18n(translations.shipped)

    case 'VALIDATED':
      return i18n(translations.validated)

    case 'PAID':
      return i18n(translations.paid)

    default:
      return ''
  }
})

const statusTagVariant = computed(() => {
  if (isOrderlineRefunded.value && props.orderline.state === 'CANCELLED') {
    return 'danger'
  }

  return 'secondary'
})

const shouldDisplayReturnStatusTag = computed(() => {
  if (productReturnStates.includes(props.productReturnState)) {
    return hasActiveProductReturn.value && !isOrderlineRefunded.value
  }

  logger.info(
    `[ASP Conversation Summary] Product return state '${props.productReturnState}' is not supported.`,
    {
      owners: ['bot-squad-seller-care-front'],
    },
  )

  return false
})
const returnStatusTagLabel = computed(() => {
  switch (props.productReturnState) {
    case 'PRODUCT_RETURN_PICKED_UP':
      return i18n(translations.productReturnPickedUp)

    case 'PRODUCT_RETURN_DELIVERED':
      return props.productReturnKind === 'CUSTOMER_TO_FACTORY'
        ? i18n(translations.deliveredToTheFactory)
        : i18n(translations.deliveredToTheSeller)

    case 'RETURN_OF_PRODUCT_RETURN_CREATED':
      return i18n(translations.returnProductReturnCreated)

    case 'RETURN_OF_PRODUCT_RETURN_PICKED_UP':
      return i18n(translations.returnProductReturnPickedUp)

    case 'RETURN_OF_PRODUCT_RETURN_DELIVERED':
      return i18n(translations.returnProductReturnDelivered)

    default:
      return ''
  }
})

const shouldDisplayRepairStatusTag = computed(() => {
  if (props.repairStatus === null) {
    return false
  }

  if (productRepairStatuses.includes(props.repairStatus)) {
    return true
  }

  logger.info(
    `[ASP Conversation Summary] Repair status '${props.repairStatus}' is not supported.`,
    {
      owners: ['bot-squad-seller-care-front'],
    },
  )

  return false
})
const repairStatusTagLabel = computed(() => {
  switch (props.repairStatus) {
    case 'CREATED':
      return i18n(translations.repairCreated)

    case 'RECEIVED':
      return i18n(translations.repairReceived)

    case 'DEVICE_LOCKED':
      return i18n(translations.repairDeviceLocked)

    case 'DEVICE_UNLOCKED':
      return i18n(translations.repairDeviceUnlocked)

    case 'OUT_OF_WARRANTY':
      return i18n(translations.repairOutOfWarranty)

    case 'REPAIRED':
      return i18n(translations.repairRepaired)

    case 'NOT_REPAIRED':
      return i18n(translations.repairNotRepaired)

    case 'SHIPPED_TO_CUSTOMER':
      return i18n(translations.repairShippedToCustomer)

    default:
      return ''
  }
})

const shouldDisplayPartnershipTag = computed(() => props.orderline.partnership)

const isSmallScreen = useMediaQuery('(max-width: 768px)', { ssrWidth: 768 })
</script>
